import { useMemo } from "react";
import type { Locale } from "date-fns";
import {
  format,
  startOfYear,
  endOfYear,
  eachWeekOfInterval,
  eachDayOfInterval,
  endOfWeek,
  getMonth,
} from "date-fns";

export interface ActivityWeekDay {
  date: Date;
  count: number;
  inYear: boolean;
}

export interface ActivityMonthLabel {
  label: string;
  colStart: number;
}

export interface UseYearlyActivityWeeksResult {
  maxCount: number;
  weeksData: ActivityWeekDay[][];
  monthLabels: ActivityMonthLabel[];
}

export const useYearlyActivityWeeks = (
  sessionCountsByDay: Map<string, number>,
  year: number,
  locale: Locale,
): UseYearlyActivityWeeksResult => {
  const maxCount = useMemo(() => {
    let max = 0;
    sessionCountsByDay.forEach((count) => {
      if (count > max) max = count;
    });
    return max || 1;
  }, [sessionCountsByDay]);

  const weeksData = useMemo(() => {
    const yearStart = startOfYear(new Date(year, 0, 1));
    const yearEnd = endOfYear(new Date(year, 0, 1));

    const weeks = eachWeekOfInterval({ start: yearStart, end: yearEnd }, { weekStartsOn: 1 });

    return weeks.map((weekStart) => {
      const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
      const days = eachDayOfInterval({ start: weekStart, end: weekEnd });

      return days.map((day) => {
        if (day.getFullYear() !== year) {
          return { date: day, count: -1, inYear: false };
        }

        const dateKey = format(day, "yyyy-MM-dd");
        const count = sessionCountsByDay.get(dateKey) || 0;
        return { date: day, count, inYear: true };
      });
    });
  }, [year, sessionCountsByDay]);

  const monthLabels = useMemo(() => {
    const labels: ActivityMonthLabel[] = [];
    let lastMonth = -1;

    weeksData.forEach((week, weekIndex) => {
      const firstDayInYear = week.find((d) => d.inYear);
      if (firstDayInYear) {
        const month = getMonth(firstDayInYear.date);
        if (month !== lastMonth) {
          lastMonth = month;
          labels.push({
            label: format(firstDayInYear.date, "MMM", { locale }),
            colStart: weekIndex,
          });
        }
      }
    });

    return labels;
  }, [weeksData, locale]);

  return { maxCount, weeksData, monthLabels };
};
